import { daysBetween, shiftMonths } from '../format/dates'
import { euro, toCents } from '../format/currency'
import type { Course } from '../types/account'
import type { Product, Treatment } from '../types/catalog'

/**
 * Rekenregels voor een vooruitbetaalde kuur (docs/KASSA-ABONNEMENTEN.md §4). Puur en getest;
 * de server rekent hetzelfde (tests/vectors/kuur.json). Bedragen in euro's in de types, intern in centen.
 */

// Dezelfde omzetting als overal (geen 1,005 × 100 = 100,4999…): anders kan de kuurstop een cent schelen met de server.
// Een half ingevuld veld in de editor (NaN) gooit hier niet, zoals voorheen: dan NaN, en de controles melden het.
const cents = (euro: number) => (Number.isFinite(euro) ? toCents(euro) : NaN)
const euros = (c: number) => c / 100

/** Een kuur vooruit betalen heeft alleen zin als hij goedkoper is dan losse sessies. */
export const kuurSaving = (sessions: number, kuurPrice: number, singlePrice: number | null) =>
  singlePrice === null ? null : euros(Math.max(0, cents(singlePrice) * sessions - cents(kuurPrice)))

/** Probleem met de kuurprijs bij een behandeling, of `null`. */
export function kuurPriceProblem(sessions: number, kuurPrice: number, singlePrice: number | null): string | null {
  if (!(kuurPrice > 0)) return 'Vul de kuurprijs in.'
  if (singlePrice !== null && cents(kuurPrice) >= cents(singlePrice) * sessions) {
    return `De kuur is niet goedkoper dan ${sessions} losse behandelingen. Klanten krijgen dan bij terugbetalen niets terug voor gedane sessies.`
  }
  return null
}

// ——— No-show ———

/**
 * Telt "Niet gekomen" bij deze afspraak als gebruikte kuursessie? Alleen met de no-show-regel aan,
 * bij een afspraak met een sessienummer van haar lopende kuur, en zolang er sessies over zijn.
 */
export function noShowUsesSession(course: Pick<Course, 'treatmentSlug' | 'done' | 'total'> | null | undefined, appointment: { treatmentSlug: string; session?: number }, ruleOn: boolean): boolean {
  return Boolean(ruleOn && course && appointment.session && course.treatmentSlug === appointment.treatmentSlug && course.done < course.total)
}

// ——— Kuur kopen in de webshop ———

const KUUR_PRODUCT_PREFIX = 'kuur-'

/** Vaste id in de winkelmand en op de bestelling: `kuur-goddess-lift`. */
export const kuurProductId = (slug: string) => `${KUUR_PRODUCT_PREFIX}${slug}`
export const isKuurProductId = (id: string) => id.startsWith(KUUR_PRODUCT_PREFIX)
export const isKuurProduct = (p: { kind?: string }) => p.kind === 'kuur'

/**
 * Een vooruitbetaalde kuur als product, zodat hij door dezelfde winkelmand, checkout en bestelling
 * gaat als een cadeaubon. Afgeleid uit `Treatment.kuur`; er wordt niets apart opgeslagen.
 * Btw 21% bij verkoop over het hele bedrag (K5). Max. 1 per bestelling (`maxQty`).
 */
export function kuurProducts(treatments: readonly Treatment[], image: string, validMonths: number): Product[] {
  return treatments
    .filter((t): t is Treatment & { kuur: { sessions: number; price: number } } => Boolean(t.kuur && t.kuur.price > 0 && !t.online))
    .map((t) => {
      const saving = kuurSaving(t.kuur.sessions, t.kuur.price, t.price)
      return {
        id: kuurProductId(t.slug),
        kind: 'kuur' as const,
        treatmentSlug: t.slug,
        name: `Kuur ${t.name} · ${t.kuur.sessions}×`,
        brand: 'Beauty Bewust',
        category: 'Kuur' as const,
        price: t.kuur.price,
        vatRate: 21,
        volume: `${t.kuur.sessions} sessies`,
        usesPerBottle: `${validMonths} maanden geldig`,
        image,
        imageAlt: `Behandelkamer van Beauty Bewust`,
        skinTypes: [],
        note: saving ? `Je bespaart ${euro(saving)} ten opzichte van losse behandelingen.` : 'Alleen als kuur te boeken.',
        why: t.tagline,
        howTo: 'Na betaling staat de kuur in Mijn omgeving. Je plant elke sessie zelf in; hij is al betaald.',
        keyIngredients: [],
        rating: 0,
        reviews: 0,
        refill: false,
        pairs: [],
      }
    })
}

/** Tot wanneer een vooruitbetaalde kuur geldig is. */
export const kuurValidUntil = (paidOn: string, months: number) => shiftMonths(paidOn, months)

export const isKuurExpired = (course: Course, today: string) => Boolean(course.prepaid && today > course.prepaid.validUntil)

/** Sessies die nog uit de vooruitbetaalde kuur mogen. */
export const prepaidLeft = (course: Course, today: string) =>
  !course.prepaid || isKuurExpired(course, today) ? 0 : Math.max(0, course.total - course.done)

/** Zoveel dagen vóór het verlopen krijgt de klant de mail "Je kuur verloopt bijna" (`kuur-expiring`). */
export const KUUR_EXPIRY_NOTICE_DAYS = 30

/**
 * Over hoeveel dagen een vooruitbetaalde kuur verloopt, als dat binnen `KUUR_EXPIRY_NOTICE_DAYS`
 * valt en er nog sessies over zijn; anders `null`. De dagelijkse job van de server stuurt de mail
 * één keer per kuur zodra dit niet `null` is (en onthoudt dat hij verstuurd is). 0 = vandaag de laatste dag.
 */
export function kuurExpiresSoon(course: Course, today: string): number | null {
  if (!course.prepaid || prepaidLeft(course, today) === 0) return null
  const days = daysBetween(today, course.prepaid.validUntil)
  return days >= 0 && days <= KUUR_EXPIRY_NOTICE_DAYS ? days : null
}

export type KuurRefund = {
  /** Wat de klant terugkrijgt, in euro's. Nooit negatief, nooit meer dan betaald. */
  amount: number
  /** Hoe het is uitgerekend, voor Sarah en op de creditbon. */
  explanation: string
}

/**
 * Terugbetalen als de klant stopt (K5, besluit okt 2026: "korting pas na de hele kuur").
 * Gedane sessies tellen tegen de losse prijs; de rest van het betaalde bedrag gaat terug.
 * Heeft de behandeling geen losse prijs (bijv. Cosmic Botox), dan tellen de gedane sessies
 * tegen het kuurtarief (betaald ÷ sessies). Een verlopen kuur geeft niets terug.
 */
export function kuurRefund(course: Course, singlePrice: number | null, today: string): KuurRefund {
  const p = course.prepaid
  if (!p) return { amount: 0, explanation: 'Deze kuur is per keer betaald.' }
  if (isKuurExpired(course, today)) return { amount: 0, explanation: 'De kuur is verlopen; de rest is vervallen.' }
  const paid = cents(p.paid)
  const done = Math.min(course.done, course.total)
  if (done >= course.total) return { amount: 0, explanation: 'Alle sessies zijn gedaan.' }
  if (singlePrice === null) {
    const used = Math.round((paid * done) / course.total)
    return {
      amount: euros(paid - used),
      explanation: `${course.total - done} van ${course.total} sessies niet gebruikt, tegen het kuurtarief.`,
    }
  }
  const used = Math.min(paid, cents(singlePrice) * done)
  return {
    amount: euros(paid - used),
    explanation:
      done === 0
        ? 'Nog geen sessie gedaan: alles terug.'
        : `Betaald min ${done} ${done === 1 ? 'sessie' : 'sessies'} tegen de losse prijs; de kuurkorting geldt alleen voor de hele kuur.`,
  }
}

// ——— Kuur stoppen en terugbetalen (§4.4) ———

export type KuurStopInput = {
  /** `undefined` = nog niets ingevuld. */
  refundCents: number | undefined
  /** Het voorstel van `kuurRefund`, in centen. */
  suggestedCents: number
  /** Wat de klant voor de kuur betaalde, in centen. */
  paidCents: number
  reason: string
}

export type KuurStopProblem = { field: 'refundCents' | 'reason'; message: string }

/**
 * Wat er niet klopt aan een terugbetaling, of `null`. Het bedrag mag afwijken van het voorstel
 * (coulance, of minder bij een afspraak), maar dan met een reden: die staat op de creditnota.
 * Nooit meer dan ze betaalde. Zelfde regel in het scherm, de demo en de server.
 */
export function kuurStopProblem(i: KuurStopInput): KuurStopProblem | null {
  if (i.refundCents === undefined || !Number.isInteger(i.refundCents) || i.refundCents < 0) {
    return { field: 'refundCents', message: 'Vul in hoeveel ze terugkrijgt (€ 0 mag).' }
  }
  if (i.refundCents > i.paidCents) return { field: 'refundCents', message: `Dat is meer dan ze betaalde (${euro(euros(i.paidCents), { cents: true })}).` }
  if (i.refundCents !== i.suggestedCents && !i.reason.trim()) {
    return { field: 'reason', message: 'Je wijkt af van het voorstel: schrijf kort waarom. Dat staat op de creditnota.' }
  }
  return null
}

/** Het voorstel van `kuurRefund` in hele centen. */
export const suggestedRefundCents = (course: Course, singlePrice: number | null, today: string) => cents(kuurRefund(course, singlePrice, today).amount)
